import React, { useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { useDragLayer } from 'react-dnd'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import CloseIcon from '@material-ui/icons/Close'
import type { DropdownSelectOption, StyleClasses } from '@paintscout/ui'

import {
  Button,
  DropdownSelect,
  EstimatorSelect,
  Grid,
  Hidden,
  IconButton,
  KeypadInputField,
  Spinner,
  TagSelection,
  Tooltip,
  Typography,
  useClientOptions
} from '@paintscout/ui'
import FilterListIcon from '@material-ui/icons/FilterList'
import { getObjectLabels } from '@paintscout/util/builder/util/get-object-labels'
import { getQuoteTypeOptions, getPipelines, getQuoteTags } from '@paintscout/util/builder/options'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder'
import type { CalendarEvent, QuoteTag } from 'paintscout'
import { useProductionCalendar } from './ProductionCalendarContext'
import { useFlags } from 'shared/components/common/FlagsProvider'
import UnscheduledJobCard from './UnscheduledJobCard'
import {
  countActiveFilterFields,
  getDesktopFilterButtonClassName,
  getMobileFilterButtonClassName
} from './filterButtonHelpers'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    customDragLayer: {
      backgroundColor: 'rgb(228, 233, 242)',
      minWidth: '200px',
      maxWidth: '300px',
      height: theme.spacing(4),
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0, 2),
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.75rem',
      color: 'rgb(31, 46, 71)',
      boxShadow: theme.shadows[4],
      border: '1px solid #d1d5db'
    },
    dragLayerText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    collapsedJobsContainerInner: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.palette.action.hover
      }
    },
    collapsedDownArrow: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      color: 'rgb(146, 159, 181)'
    },
    collapsedButtonInnerText: {
      textAlign: 'left'
    },
    unscheduledJobsContainer: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1.5)
      }
    },
    unscheduledJobsContainerHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingBottom: theme.spacing(2),
      minWidth: 0,
      [theme.breakpoints.down('xs')]: {
        flexWrap: 'wrap',
        gap: theme.spacing(1.5)
      }
    },
    unscheduledJobsContainerInner: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(1)
      }
    },
    collapseButton: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.5)
      }
    },
    unscheduledJobsTextContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      whiteSpace: 'nowrap'
    },
    unscheduledFilterButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 0
    },
    unscheduledCloseIcon: {
      width: theme.spacing(2),
      height: theme.spacing(2)
    },
    filterButton: {
      height: theme.spacing(4.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 3)
    },
    mobileFilterButton: {
      position: 'relative',
      paddingRight: 0,
      paddingLeft: 0
    },
    filterButtonSecondary: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
      '&&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:focus': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P80
      }
    },
    filterButtonPrimary: {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
      '&&:hover': {
        backgroundColor: theme.palette.primary.dark
      },
      '&&:active': {
        backgroundColor: theme.palette.primary.main
      },
      '&&:focus': {
        backgroundColor: theme.palette.primary.main
      }
    },
    mobileFilterCount: {
      position: 'absolute',
      top: -5,
      right: -5,
      border: `1px solid ${theme.palette.background.paper}`,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '9999px',
      height: theme.spacing(2.5),
      width: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      color: theme.palette.background.paper
    },
    filterCount: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      borderRadius: '9999px',
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.25),
      marginLeft: theme.spacing(1)
    },
    unscheduledFilterContainer: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96
    },
    unscheduledFilterInputContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(4)
      }
    },
    unscheduledFilterTopDiv: {
      display: 'grid',
      gap: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '1fr 1fr'
      }
    },
    unscheduledFilterInnerButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
      paddingTop: theme.spacing(3),
      borderTop: `1px solid #d9dee8`
    },
    noResultsMessage: {
      // "py-8 text-center"
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(8),
      textAlign: 'center'
    },

    noResultsMessageButton: {
      marginTop: theme.spacing()
    },
    noResultsMessageButtonText: {
      color: theme.paintscout.palette.brand.Blue.Blue65
    },
    jobCardContainer: {
      // "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
      },
      [theme.breakpoints.up('xl')]: {
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))'
      }
    },
    hasMoreContainer: {
      // "mt-4 flex justify-center"
      marginTop: theme.spacing(2),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    showMoreIcon: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      marginRight: theme.spacing(1)
    }
  }),
  { name: 'UnscheduledJobs' }
)

function CustomDragLayer() {
  const classes = useStyles()
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset()
  }))

  if (!isDragging || !currentOffset || !item?.event || !item.isUnscheduled) {
    return null
  }

  // Snap x to the left edge of the hovered date cell, shifted one cell left
  // via previousElementSibling to match where the block will actually land.
  // Falls back to cursor position when not over the schedule grid.
  let snappedX = currentOffset.x
  for (const el of document.elementsFromPoint(currentOffset.x, currentOffset.y)) {
    const dayCell = el.closest('[data-date]')
    if (dayCell) {
      const prevCell = dayCell.previousElementSibling
      const targetCell = prevCell?.hasAttribute('data-date') ? prevCell : dayCell
      snappedX = (targetCell as HTMLElement).getBoundingClientRect().left
      break
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: snappedX,
        top: currentOffset.y,
        transform: 'translateY(-50%)'
      }}
    >
      <div className={classes.customDragLayer}>
        <div className={classes.dragLayerText}>{item.event.title}</div>
      </div>
    </div>
  )
}

export interface UnscheduledJobsProps {
  classes?: StyleClasses<typeof useStyles>
}

function getRowsForPage(args: { rows: any[]; page: number; rowsPerPage: number }) {
  const { rows, page, rowsPerPage } = args
  return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
}

function UnscheduledJobs() {
  const classes = useStyles()
  const { options } = useClientOptions()
  const {
    updateEvent,
    unscheduledEvents,
    totalUnscheduledEvents,
    fetchMoreUnscheduledEvents,
    loading,
    unscheduledFilters
  } = useProductionCalendar()
  const { canDeactivateSettingsItems } = useFlags()
  const crews = getCrews({ options })
  const { quote: quoteLabel, currency } = getObjectLabels({ options })
  const typeOptions = getQuoteTypeOptions({ options, hideAll: false, canDeactivate: !!canDeactivateSettingsItems }).map(
    (type) => ({
      value: type.key,
      label: type.label
    })
  )
  const productionPipeline = getPipelines({ options })?.find((pipeline) => pipeline.key === 'production-pipeline')
  const stageOptions = (productionPipeline?.stages || []).map((type) => ({
    value: type.key,
    label: type.label
  }))

  const tagOptions = getQuoteTags({ options })

  const currentPage = useRef(1)
  const [pageChange, setPageChange] = useState(true)
  const [showingMore, setShowingMore] = useState(currentPage.current > 1 || unscheduledEvents.length > 8)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showFilters, setShowFilters] = useState(false)

  const rowsPerPage = showingMore ? 8 : 4
  const hasMore = unscheduledEvents.length > 4
  const pages = Math.ceil(totalUnscheduledEvents / rowsPerPage)
  const [rowsForPage, setRowsForPage] = useState(
    getRowsForPage({ rows: unscheduledEvents, rowsPerPage, page: currentPage.current - 1 })
  )
  const isFirstPage = currentPage.current <= 1
  const isLastPage = currentPage.current >= pages || !pages || pages === 1

  const [localMinPrice, setLocalMinPrice] = useState(unscheduledFilters.unscheduledMinPrice)
  const [localMaxPrice, setLocalMaxPrice] = useState(unscheduledFilters.unscheduledMaxPrice)
  const [localMinHours, setLocalMinHours] = useState(unscheduledFilters.unscheduledMinHours)
  const [localMaxHours, setLocalMaxHours] = useState(unscheduledFilters.unscheduledMaxHours)
  const [localSelectedProjectTypes, setLocalSelectedProjectTypes] = useState(
    unscheduledFilters.unscheduledSelectedProjectTypes
  )
  const [localSelectedProjectManagers, setLocalSelectedProjectManagers] = useState<string[]>(
    unscheduledFilters.unscheduledSelectedProjectManagers
  )
  const [localSelectedStages, setLocalSelectedStages] = useState(unscheduledFilters.unscheduledSelectedStages)
  const [localSelectedTags, setLocalSelectedTags] = useState(unscheduledFilters.unscheduledTags)

  // Update filtered events whenever filters or unscheduledEvents change
  React.useEffect(() => {
    setRowsForPage(getRowsForPage({ rows: unscheduledEvents, rowsPerPage, page: currentPage.current - 1 }))
  }, [
    unscheduledFilters.unscheduledSelectedProjectTypes,
    unscheduledFilters.unscheduledMinPrice,
    unscheduledFilters.unscheduledMaxPrice,
    unscheduledFilters.unscheduledMinHours,
    unscheduledFilters.unscheduledMaxHours,
    unscheduledFilters.unscheduledSelectedProjectManagers,
    unscheduledFilters.unscheduledSelectedStages,
    unscheduledFilters.unscheduledTags,
    unscheduledEvents.length,
    pageChange,
    showingMore
  ])

  const hasActiveFilters =
    unscheduledFilters.unscheduledSelectedProjectManagers.length > 0 ||
    unscheduledFilters.unscheduledSelectedProjectTypes.length > 0 ||
    unscheduledFilters.unscheduledMinPrice ||
    unscheduledFilters.unscheduledMaxPrice ||
    unscheduledFilters.unscheduledMinHours ||
    unscheduledFilters.unscheduledMaxHours ||
    unscheduledFilters.unscheduledSelectedStages.length > 0 ||
    unscheduledFilters.unscheduledTags.length > 0
  const activeFilterCount = countActiveFilterFields([
    Boolean(localMinPrice),
    Boolean(localMaxPrice),
    Boolean(localMinHours),
    Boolean(localMaxHours),
    localSelectedProjectTypes.length > 0,
    localSelectedProjectManagers.length > 0,
    localSelectedStages.length > 0,
    localSelectedTags.length > 0
  ])
  const desktopFilterButtonClassName = getDesktopFilterButtonClassName({ classes, isOpen: showFilters })
  const mobileFilterButtonClassName = getMobileFilterButtonClassName({ classes, isOpen: showFilters })

  const clearFilters = () => {
    setShowFilters(false)
    unscheduledFilters.setUnscheduledSelectedProjectManagers([])
    unscheduledFilters.setUnscheduledSelectedProjectTypes([])
    unscheduledFilters.setUnscheduledMinPrice('')
    unscheduledFilters.setUnscheduledMaxPrice('')
    unscheduledFilters.setUnscheduledMinHours('')
    unscheduledFilters.setUnscheduledMaxHours('')
    unscheduledFilters.setUnscheduledSelectedStages([])
    unscheduledFilters.setUnscheduledTags([])
    setLocalSelectedProjectManagers([])
    setLocalSelectedStages([])
    setLocalSelectedTags([])
    setLocalSelectedProjectTypes([])
    setLocalMinPrice(null)
    setLocalMaxPrice(null)
    setLocalMinHours(null)
    setLocalMaxHours(null)
  }

  const handleSchedule = (eventId: string, startDate: number, endDate: number, crewId: string) => {
    const newCrew = crews.find((c) => c._id === crewId) || null
    const event = unscheduledEvents.find((e) => e.id === eventId)
    if (!event || !newCrew) return
    const updatedEvent: CalendarEvent = {
      ...event,
      crew: newCrew._id,
      start: new Date(startDate),
      end: new Date(endDate)
    }

    updateEvent(updatedEvent)
  }

  function handleChangePage(event, nextPage) {
    if (nextPage === 0 || nextPage > pages) return
    const nextPageFirstIndex = (nextPage - 1) * rowsPerPage
    const dataExists = !!unscheduledEvents[nextPageFirstIndex]

    if (!dataExists && fetchMoreUnscheduledEvents) {
      fetchMoreUnscheduledEvents()
    }

    currentPage.current = nextPage
    setPageChange(!pageChange)
  }

  function handleSearch() {
    // Apply local filter values to the global unscheduledFilters
    // only on the values that have changed to avoid unnecessary re-filtering

    if (localMinPrice !== unscheduledFilters.unscheduledMinPrice) {
      unscheduledFilters.setUnscheduledMinPrice(String(localMinPrice))
    }
    if (localMaxPrice !== unscheduledFilters.unscheduledMaxPrice) {
      unscheduledFilters.setUnscheduledMaxPrice(String(localMaxPrice))
    }
    if (localMinHours !== unscheduledFilters.unscheduledMinHours) {
      unscheduledFilters.setUnscheduledMinHours(String(localMinHours))
    }
    if (localMaxHours !== unscheduledFilters.unscheduledMaxHours) {
      unscheduledFilters.setUnscheduledMaxHours(String(localMaxHours))
    }
    if (localSelectedProjectTypes !== unscheduledFilters.unscheduledSelectedProjectTypes) {
      unscheduledFilters.setUnscheduledSelectedProjectTypes(localSelectedProjectTypes)
    }
    if (localSelectedProjectManagers !== unscheduledFilters.unscheduledSelectedProjectManagers) {
      unscheduledFilters.setUnscheduledSelectedProjectManagers(localSelectedProjectManagers)
    }
    if (localSelectedStages !== unscheduledFilters.unscheduledSelectedStages) {
      unscheduledFilters.setUnscheduledSelectedStages(localSelectedStages)
    }
    if (localSelectedTags !== unscheduledFilters.unscheduledTags) {
      unscheduledFilters.setUnscheduledTags(localSelectedTags)
    }

    setShowFilters(false)
  }

  if (unscheduledEvents.length === 0 && !unscheduledFilters) {
    return null
  }

  if (isCollapsed) {
    return (
      <div className={classes.unscheduledJobsContainer}>
        <div className={classes.collapsedJobsContainerInner} onClick={() => setIsCollapsed(false)}>
          <IconButton onClick={() => setIsCollapsed(false)}>
            <KeyboardArrowDownIcon className={classes.collapsedDownArrow} />
          </IconButton>

          <div className={classes.unscheduledJobsTextContainer}>
            <Typography variant="h3">Unscheduled ({totalUnscheduledEvents})</Typography>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.unscheduledJobsContainer}>
      <CustomDragLayer />
      <div className={classes.unscheduledJobsContainerHeader}>
        <div className={classes.unscheduledJobsContainerInner}>
          <IconButton
            onClick={() => {
              setIsCollapsed((isCollapsed) => !isCollapsed)
              setShowingMore(false)
            }}
            className={classes.collapseButton}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
          <div className={classes.unscheduledJobsTextContainer}>
            <Typography variant="h3">
              Unscheduled ({totalUnscheduledEvents}){/* <span className="relative group/tooltip"> */}
            </Typography>
            {!isCollapsed && (
              <Tooltip interactive title="" content={'All deals without a job that are in the production pipeline.'} />
            )}
          </div>
        </div>

        <div className={classes.unscheduledFilterButtonContainer}>
          {hasActiveFilters && (
            <>
              <Hidden xsDown>
                <Button variant="text" onClick={clearFilters} title="Clear all filters" icon={CloseIcon}>
                  <Typography variant="subtitle1">Clear</Typography>
                </Button>
              </Hidden>
              <Hidden smUp>
                <IconButton onClick={clearFilters} title="Clear all filters" size="small">
                  <CloseIcon className={classes.unscheduledCloseIcon} />
                </IconButton>
              </Hidden>
            </>
          )}
          <>
            <Hidden xsDown>
              <div className={classes.mobileFilterButton}>
                <Button
                  variant="contained"
                  icon={FilterListIcon}
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  className={desktopFilterButtonClassName}
                >
                  <Typography variant="body1">Filter</Typography>
                </Button>
                {hasActiveFilters && <span className={classes.mobileFilterCount}>{activeFilterCount}</span>}
              </div>
            </Hidden>
            <Hidden smUp>
              <div className={classes.mobileFilterButton}>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(!showFilters)}
                  className={mobileFilterButtonClassName}
                  title="Filter"
                >
                  <FilterListIcon />
                </Button>
                {hasActiveFilters && <span className={classes.mobileFilterCount}>{activeFilterCount}</span>}
              </div>
            </Hidden>
          </>
        </div>
      </div>

      {showFilters && (
        <div className={classes.unscheduledFilterContainer}>
          <div className={classes.unscheduledFilterInputContainer}>
            {/* Price Range and Hours Range - Side by Side */}
            <Grid container alignItems="flex-end" spacing={2}>
              {/* Price Range */}
              <Grid item sm={3} xs={6}>
                <KeypadInputField
                  label={`Amount (${currency.symbol})`}
                  margin="none"
                  placeholder="Min"
                  format="dimension"
                  fullWidth
                  allowClear
                  value={localMinPrice}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = event.target
                    setLocalMinPrice(Number(value))
                  }}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <KeypadInputField
                  margin="none"
                  flipX
                  placeholder="Max"
                  format="dimension"
                  value={localMaxPrice}
                  fullWidth
                  allowClear
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = event.target
                    setLocalMaxPrice(Number(value))
                  }}
                />
              </Grid>
              {/* Hours Range */}
              <Grid item sm={3} xs={6}>
                <KeypadInputField
                  label={`Hours`}
                  margin="none"
                  placeholder="Min"
                  format="dimension"
                  fullWidth
                  allowClear
                  value={localMinHours}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = event.target
                    setLocalMinHours(Number(value))
                  }}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <KeypadInputField
                  margin="none"
                  flipX
                  placeholder="Max"
                  format="dimension"
                  value={localMaxHours}
                  fullWidth
                  allowClear
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = event.target
                    setLocalMaxHours(Number(value))
                  }}
                />
              </Grid>
            </Grid>
            {/* Estimate Type and Estimator - Side by Side */}
            {/* Project Type Filter */}
            <Grid container alignItems="flex-end" spacing={2}>
              <Grid item xs={12} lg={3}>
                <DropdownSelect
                  label={`${quoteLabel.value} Type`}
                  variant="multi"
                  options={typeOptions}
                  hideCloseButton
                  placeholder={{
                    selectPlaceholder: `Select ${quoteLabel.value.toLowerCase()} type(s)`,
                    plural: `Types`
                  }}
                  value={localSelectedProjectTypes.map((type) => {
                    return {
                      value: type,
                      label: typeOptions.find((opt) => opt.value === type)?.label || ''
                    }
                  })}
                  onChange={(selectedOptions) => {
                    setLocalSelectedProjectTypes(selectedOptions.map((option) => option.value))
                  }}
                />
              </Grid>

              {/* Sales Rep Filter */}
              <Grid item xs={12} lg={3}>
                <EstimatorSelect
                  name="estimator"
                  label="Estimator"
                  variant="multi"
                  value={localSelectedProjectManagers?.map((val) => addAuth0Prefix(val as string))}
                  roles={['sales', 'admin']}
                  placeholder={{
                    selectPlaceholder: 'Select estimator(s)',
                    plural: 'Estimators'
                  }}
                  onChange={(options: DropdownSelectOption[]) => {
                    setLocalSelectedProjectManagers(options.map((option) => removeAuth0Prefix(option.value)))
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <DropdownSelect
                  label="Stage"
                  variant="multi"
                  options={stageOptions}
                  hideCloseButton
                  onChange={(options: DropdownSelectOption[]) => {
                    setLocalSelectedStages(options.map((option) => option.value))
                  }}
                  value={localSelectedStages.map((stage) => {
                    return {
                      value: stage,
                      label: stageOptions.find((opt) => opt.value === stage)?.label || ''
                    }
                  })}
                  placeholder={{
                    selectPlaceholder: `Select stage(s)`,
                    plural: `Stages`
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <TagSelection
                  label="Tags"
                  appliedTags={localSelectedTags.map((tag) => tagOptions.find((opt) => opt.name === tag))}
                  handleChange={(tags: QuoteTag[]) => {
                    setLocalSelectedTags(tags.map((tag) => tag.name))
                  }}
                  placeholder={{
                    selectPlaceholder: `Select tag(s)`,
                    plural: `Stages`
                  }}
                />
              </Grid>
            </Grid>
          </div>

          {/* Action Buttons */}
          <div className={classes.unscheduledFilterInnerButtonContainer}>
            <Button variant="text" onClick={clearFilters}>
              Clear
            </Button>
            <Button variant="contained" onClick={() => handleSearch()}>
              Search
            </Button>
          </div>
        </div>
      )}

      {unscheduledEvents.length === 0 && hasActiveFilters ? (
        <div className={classes.noResultsMessage}>
          <Typography variant={'body1'} color="textSecondary">
            No jobs match the selected filters
          </Typography>
          <Button variant={'text'} fullWidth={false} onClick={clearFilters} className={classes.noResultsMessageButton}>
            <Typography variant="subtitle1" className={classes.noResultsMessageButtonText}>
              Clear all filters
            </Typography>
          </Button>
        </div>
      ) : (
        <>
          <div className={classes.jobCardContainer}>
            {loading && <Spinner />}

            {!loading &&
              rowsForPage.map((event) => (
                <UnscheduledJobCard key={event.id} event={event} onSchedule={handleSchedule} />
              ))}
          </div>

          {hasMore && (
            <div className={classes.hasMoreContainer}>
              {showingMore ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button
                    variant="text"
                    disabled={loading || isFirstPage}
                    onClick={() => handleChangePage(null, currentPage.current - 1)}
                  >
                    Prev
                  </Button>
                  <Typography variant="body2" style={{ margin: '0 8px' }}>
                    Page {currentPage.current} of {pages}
                  </Typography>
                  <Button
                    variant="text"
                    disabled={loading || isLastPage}
                    onClick={() => handleChangePage(null, currentPage.current + 1)}
                  >
                    Next
                  </Button>
                </div>
              ) : (
                <Button
                  variant={'text'}
                  onClick={() => {
                    setShowingMore((showingMore) => !showingMore)
                  }}
                >
                  {showingMore ? (
                    <>
                      <KeyboardArrowUpIcon className={classes.showMoreIcon} />
                      <Typography variant="body2">Show Less</Typography>
                    </>
                  ) : (
                    <>
                      <KeyboardArrowDownIcon className={classes.showMoreIcon} />
                      <Typography variant="body2">
                        View {unscheduledEvents.length - 4} More {unscheduledEvents.length - 4 === 1 ? 'Job' : 'Jobs'}
                      </Typography>
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default UnscheduledJobs
